import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  GripVertical,
  Trash2Icon,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RichTextEditor } from "@/components/RichTextEditor/RichTextEditor";
import {
  COURSE_CONTENT_BLOCK_TYPES,
  CourseContentBlockType,
} from "@/modules/learningCenter/features/CourseManagement/types/course";
import { CourseFormBlock } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import { CalloutBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/CalloutBlockEditor";
import { ReflectionBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/ReflectionBlockEditor";
import { KnowledgeCheckBlockEditor } from "@/modules/learningCenter/features/CourseManagement/components/CourseContentBlockEditor/KnowledgeCheckBlockEditor";

interface CourseContentBlockEditorProps {
  blocks: CourseFormBlock[];
  onChange: (blocks: CourseFormBlock[]) => void;
}

const BLOCK_LABELS: Record<CourseContentBlockType, string> = {
  text: "Text",
  image: "Image",
  file: "File",
  video: "Video",
  callout: "Callout",
  reflection: "Reflection",
  knowledge_check: "Knowledge Check",
};

const iconButtonClass =
  "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none";

interface SortableBlockCardProps {
  block: CourseFormBlock;
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
  children: React.ReactNode;
}

/**
 * One block's card. Only the grip in the header starts a drag, so selecting
 * text or clicking into an editor never picks the block up; the arrows are
 * the precise, one-step alternative.
 */
function SortableBlockCard({
  block,
  index,
  count,
  onMove,
  onRemove,
  children,
}: SortableBlockCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const n = index + 1;

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800"
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: "relative",
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            aria-label={`Reorder block ${n}`}
            className={`${iconButtonClass} cursor-grab -ml-1 px-1`}
            type="button"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {BLOCK_LABELS[block.type]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            aria-label={`Move block ${n} up`}
            className={iconButtonClass}
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            type="button"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
          <button
            aria-label={`Move block ${n} down`}
            className={iconButtonClass}
            disabled={index === count - 1}
            onClick={() => onMove(index, index + 1)}
            type="button"
          >
            <ArrowDownIcon className="h-4 w-4" />
          </button>
          <button
            aria-label="Remove block"
            className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            onClick={onRemove}
            type="button"
          >
            <Trash2Icon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {children}
    </div>
  );
}

/**
 * Authoring surface for a module's content blocks. Supports the same block set
 * the course backend validates (`CourseContentBlock::TYPES`) and reuses the
 * shared `RichTextEditor` for the rich-text `text` block type.
 */
export function CourseContentBlockEditor({
  blocks,
  onChange,
}: CourseContentBlockEditorProps) {
  const handleAddBlock = useCallback(
    (type: CourseContentBlockType) => {
      const newBlock: CourseFormBlock = {
        id: crypto.randomUUID(),
        uuid: null,
        type,
        content: "",
        metadata: null,
      };
      onChange([...blocks, newBlock]);
    },
    [blocks, onChange],
  );

  const handleRemoveBlock = useCallback(
    (id: string) => {
      onChange(blocks.filter((block) => block.id !== id));
    },
    [blocks, onChange],
  );

  const handleContentChange = useCallback(
    (id: string, content: string) => {
      onChange(
        blocks.map((block) =>
          block.id === id ? { ...block, content } : block,
        ),
      );
    },
    [blocks, onChange],
  );

  const handleMetadataChange = useCallback(
    (id: string, metadata: Record<string, unknown> | null) => {
      onChange(
        blocks.map((block) =>
          block.id === id ? { ...block, metadata } : block,
        ),
      );
    },
    [blocks, onChange],
  );

  // Block order is the array order; the course/library mappers save each
  // block's index as its orderNumber, so moving here is all a reorder needs.
  const handleMoveBlock = useCallback(
    (from: number, to: number) => {
      if (to < 0 || to >= blocks.length || from === to) return;
      onChange(arrayMove(blocks, from, to));
    },
    [blocks, onChange],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      handleMoveBlock(
        blocks.findIndex((b) => b.id === active.id),
        blocks.findIndex((b) => b.id === over.id),
      );
    },
    [blocks, handleMoveBlock],
  );

  const renderBlockEditor = (block: CourseFormBlock) => {
    switch (block.type) {
      case "text":
        return (
          <RichTextEditor
            id={block.id}
            initialContent={block.content ?? ""}
            onChange={(content) => handleContentChange(block.id, content)}
            onRemove={() => handleRemoveBlock(block.id)}
          />
        );

      case "callout":
        return (
          <CalloutBlockEditor
            block={block}
            onContentChange={(content) =>
              handleContentChange(block.id, content)
            }
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "reflection":
        return (
          <ReflectionBlockEditor
            block={block}
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "knowledge_check":
        return (
          <KnowledgeCheckBlockEditor
            block={block}
            onMetadataChange={(metadata) =>
              handleMetadataChange(block.id, metadata)
            }
          />
        );

      case "image":
      case "file":
      case "video":
      default:
        return (
          <Input
            onChange={(e) => handleContentChange(block.id, e.target.value)}
            placeholder={
              block.type === "video"
                ? "Enter URL..."
                : "Enter file reference / URL..."
            }
            value={block.content ?? ""}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {COURSE_CONTENT_BLOCK_TYPES.map((type) => (
          <Button
            key={type}
            onClick={() => handleAddBlock(type)}
            size="sm"
            type="button"
            variant="outline"
          >
            Add {BLOCK_LABELS[type]}
          </Button>
        ))}
      </div>

      {blocks.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No content blocks yet. Add one above.
        </p>
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={blocks.map((block) => block.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {blocks.map((block, index) => (
                <SortableBlockCard
                  block={block}
                  count={blocks.length}
                  index={index}
                  key={block.id}
                  onMove={handleMoveBlock}
                  onRemove={() => handleRemoveBlock(block.id)}
                >
                  {renderBlockEditor(block)}
                </SortableBlockCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
